import { observer } from "mobx-react-lite"
import {
  CruiseSearchResult,
  filterCruises,
  formatCruiseFilterDate,
  getCruiseFilterData,
  SelectedFilters,
} from "@cruisen/react-shared"
import styles from "./filteringModal.module.scss"
import { useMemo } from "react"
import { CruisesFilteringModalStore } from "./FilteringModalStore"
import { ModalCheckbox } from "../../atoms/ModalCheckbox"
import { _t } from "@cruisen/i18n"
import { RangeSlider } from "../../atoms/RangeSlider"
import { useStores } from "../../../models/helpers/useStores"
import { CabinGradeType, TripSearchResultTag } from "@cruisen/contract"
import { MultiSelectInput } from "../../atoms/MultiSelectInput"
import { Accordion } from "../../atoms/Accordion"
import { CruiseDatePicker } from "../../atoms/CruiseDatePicker"
import { useForm } from "react-hook-form"
import { colors } from "@theme/colors"

const getFilteringPorts = (cruises: CruiseSearchResult[]) => {
  const originPorts = [
    ...cruises.reduce((acc, cruise) => {
      if (!cruise?.ports?.length) return acc
      acc.add(cruise.ports[0])
      return acc
    }, new Set<string>()),
  ]
    .sort()
    .map((el, index) => ({ name: el, id: index }))

  const destinationPorts = [
    ...cruises.reduce((acc, cruise) => {
      if (!cruise?.ports?.length) return acc
      cruise.ports.forEach((el) => {
        if (!originPorts.some((port) => port.name === el)) acc.add(el)
      })
      return acc
    }, new Set<string>()),
  ]
    .sort()
    .map((el, index) => ({ name: el, id: index }))

  return { originPorts, destinationPorts }
}

const getGroupedShips = (cruises: CruiseSearchResult[]) => {
  const groupedShips = cruises.reduce(
    (acc, cruise) => {
      if (!acc[cruise.lineName]) {
        acc[cruise.lineName] = new Set<string>()
      }
      acc[cruise.lineName].add(cruise.shipName)
      return acc
    },
    {} as Record<string, Set<string>>,
  )

  return Object.entries(groupedShips)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cruiseLine, ships]) => ({
      cruiseLine,
      ships: Array.from(ships).sort(),
    }))
}

const getPortSelectLabel = (selections: { id: string | number; name: string }[]) => {
  if (selections.length === 0) return ""
  if (selections.length === 1) return selections[0].name
  return `${selections.length} locations`
}

const cabinGrades: { id: CabinGradeType; label: string }[] = [
  {
    id: "inside",
    label: "Inside",
  },
  {
    id: "outside",
    label: "Outside",
  },
  {
    id: "balcony",
    label: "Balcony",
  },
  {
    id: "suite",
    label: "Suite",
  },
] as const

export const FilteringModal = observer(
  ({
    cruises,
    currentFilters,
    updateFilters,
    onClose,
    setIsDragging,
    setIsDatePickerOpen,
  }: {
    cruises: CruiseSearchResult[]
    currentFilters: SelectedFilters | null
    onClose: () => void
    updateFilters: (val: SelectedFilters) => void
    setIsDragging: (value: boolean) => void
    setIsDatePickerOpen: (value: boolean) => void
  }) => {
    const { loggedInUser, activeTrip } = useStores()
    const { initialFilterData, originPorts, destinationPorts, groupedShips } = useMemo(() => {
      const { originPorts, destinationPorts } = getFilteringPorts(cruises)
      return {
        initialFilterData: getCruiseFilterData(cruises),
        originPorts,
        destinationPorts,
        groupedShips: getGroupedShips(cruises),
      }
    }, [])
    const state = useMemo(() => new CruisesFilteringModalStore(currentFilters), [])

    const selectedOriginPorts = originPorts.filter((port) =>
      state.selectedFilters.selectedOriginPorts.includes(port.name),
    )

    const selectedDestinationPorts = destinationPorts.filter((port) =>
      state.selectedFilters.selectedDestinationPorts.includes(port.name),
    )

    const filteredCruisesCount = filterCruises(cruises, state.selectedFilters, [
      loggedInUser?.id,
    ]).length

    const { hasAgentLikedCruise, allTags } = cruises.reduce(
      (acc, cruise) => {
        if (cruise.likes.some((like) => like === loggedInUser?.id)) {
          acc.hasAgentLikedCruise = true
        }

        cruise.tags?.forEach((tag) => acc.allTags.add(tag))

        return acc
      },
      { hasAgentLikedCruise: false, allTags: new Set<TripSearchResultTag>() },
    )

    const { control, setValue } = useForm({
      defaultValues: {
        startDate: formatCruiseFilterDate(state.selectedStartDate),
        endDate: formatCruiseFilterDate(state.selectedEndDate),
      },
      mode: "all",
    })

    const onSaveDates = (dates: [string | null, string | null]) => {
      const [start, end] = dates
      state.setSelectedStartDate(start)
      state.setSelectedEndDate(end)
      setValue("startDate", formatCruiseFilterDate(start))
      setValue("endDate", formatCruiseFilterDate(end))
    }

    const availableMonths = activeTrip?.months ? [...activeTrip.months].sort() : []

    return (
      <div>
        <div
          className={styles.scrollableContainer}
          data-testid="cruises-filtering-scroll-view"
          data-modal-visible={state.visibleModal !== null}
        >
          <div className={styles.filterSection}>
            <p className={styles.sectionTitle}>Dates</p>
            <CruiseDatePicker
              availableMonths={availableMonths}
              initialStartDate={state.selectedStartDate}
              initialEndDate={state.selectedEndDate}
              control={control}
              onSave={onSaveDates}
              setIsDatePickerOpen={setIsDatePickerOpen}
            />
            {(state.selectedStartDate || state.selectedEndDate) && (
              <button
                onClick={() => {
                  state.setSelectedStartDate(null)
                  state.setSelectedEndDate(null)
                  setValue("startDate", "")
                  setValue("endDate", "")
                }}
                className={styles.clearButton}
              >
                Clear
              </button>
            )}
          </div>
          {initialFilterData?.price &&
            initialFilterData.price.min !== initialFilterData.price.max && (
              <>
                <div className={styles.filterSection}>
                  <p className={styles.sectionTitle}>Price</p>
                  <RangeSlider
                    range={initialFilterData.price}
                    values={{
                      min: state.priceFilters.low || initialFilterData.price.min,
                      max: state.priceFilters.high || initialFilterData.price.max,
                    }}
                    priceSliderStep={100}
                    valueType="price"
                    onValuesChangeFinish={(low, high) =>
                      state.changeFilters(low, high, "price", initialFilterData.price)
                    }
                    setIsDragging={setIsDragging}
                  />
                </div>
              </>
            )}
          {hasAgentLikedCruise || allTags.size > 0 ? (
            <div>
              <p className={styles.sectionTitle}>Tags</p>
              <div className={styles.verticalLayout}>
                {hasAgentLikedCruise && (
                  <ModalCheckbox
                    isChecked={state.selectedTags?.includes("agentPick")}
                    onPress={() => state.toggleTagSelection("agentPick")}
                    key="agent-picked-tag"
                    label="Advisor pick"
                  />
                )}
                {allTags.has("priceDrop") && (
                  <ModalCheckbox
                    isChecked={state.selectedTags?.includes("priceDrop")}
                    onPress={() => state.toggleTagSelection("priceDrop")}
                    key="price-drop-tag"
                    label="Price drop"
                  />
                )}
              </div>
            </div>
          ) : null}
          <div className={styles.filterSection}>
            <p className={styles.sectionTitle}>Cabin Availability</p>
            <div className={styles.verticalLayout}>
              {cabinGrades.map((option) => (
                <ModalCheckbox
                  isChecked={(state.selectedCabinGradeTypes || []).includes(option.id)}
                  onPress={() => state.setSelectedCabinGradeTypes(option.id)}
                  key={option.id}
                  label={option.label}
                />
              ))}
            </div>
          </div>
          <div className={styles.filterSection}>
            <p className={styles.sectionTitle}>Duration</p>
            <div className={styles.verticalLayout}>
              {initialFilterData?.durations.map((option) => (
                <ModalCheckbox
                  isChecked={state.selectedDurations.includes(option.id)}
                  onPress={() => state.toggleSelectedDurations(option.id)}
                  key={option.id}
                  label={option.label}
                />
              ))}
            </div>
          </div>
          <div className={styles.filterSection}>
            <p className={styles.sectionTitle}>{_t("cruiseFilteringModal.cruiseLines")}</p>
            <div className={styles.verticalLayout}>
              {initialFilterData?.cruiseLineNames.map((line) => (
                <ModalCheckbox
                  isChecked={state.selectedCruiseLines.includes(line)}
                  onPress={() => state.toggleSelectedCruiseLines(line)}
                  data-testid={`filter-cruise-line-${line.toLowerCase()}`}
                  key={line}
                  label={line}
                />
              ))}
            </div>
          </div>
          <div className={styles.filterSection}>
            <p className={styles.sectionTitle}>Ports</p>
            <div className={styles.verticalLayout}>
              <MultiSelectInput
                options={originPorts.map((port) => ({
                  label: port.name,
                  value: String(port.id),
                }))}
                label="Origin ports"
                searchLabel="Search for an origin port"
                darkTheme
                onChange={(value) => state.setSelectedOriginPorts(value.map((el) => el.name))}
                value={selectedOriginPorts}
                textValue={getPortSelectLabel(selectedOriginPorts)}
                handlePopoverChange={(visible) =>
                  state.setVisibleModal(visible ? "originPorts" : null)
                }
                name="originPorts"
                parentBackgroundColor={colors.backgroundQuaternary}
                lightOutline={true}
              />
              <div className={styles.wrapper}>
                <MultiSelectInput
                  options={destinationPorts.map((port) => ({
                    label: port.name,
                    value: String(port.id),
                  }))}
                  label="Destination ports"
                  searchLabel="Search for a destination port"
                  darkTheme
                  onChange={(value) =>
                    state.setSelectedDestinationPorts(value.map((el) => el.name))
                  }
                  value={selectedDestinationPorts}
                  textValue={getPortSelectLabel(selectedDestinationPorts)}
                  handlePopoverChange={(visible) =>
                    state.setVisibleModal(visible ? "destinationPorts" : null)
                  }
                  name="destinationPorts"
                  parentBackgroundColor={colors.backgroundQuaternary}
                  lightOutline={true}
                />
              </div>
            </div>
          </div>
          <div className={styles.filterSection}>
            <p className={styles.sectionTitle}>{_t("cruiseFilteringModal.ships")}</p>
            <div className={styles.verticalLayout}>
              {groupedShips.map(({ cruiseLine, ships }) => {
                const isActive = ships.some((ship) => state.selectedShips.includes(ship))

                return (
                  <Accordion key={cruiseLine} title={cruiseLine} isActive={isActive}>
                    <div className={styles.verticalLayout}>
                      {ships.map((ship) => (
                        <ModalCheckbox
                          data-testid={`filter-ship-${ship.toLowerCase()}`}
                          isChecked={state.selectedShips.includes(ship)}
                          onPress={() => state.toggleSelectedShips(ship)}
                          key={ship}
                          label={ship}
                        />
                      ))}
                    </div>
                  </Accordion>
                )
              })}
            </div>
          </div>
          {initialFilterData?.shipLaunchDates &&
            initialFilterData.shipLaunchDates.min !== initialFilterData.shipLaunchDates.max && (
              <>
                <div className={styles.filterSection}>
                  <p className={styles.sectionTitle}>{_t("cruiseFilteringModal.shipLaunchYear")}</p>
                  <RangeSlider
                    range={initialFilterData.shipLaunchDates}
                    values={{
                      min: state.shipYearFilters.low || initialFilterData.shipLaunchDates.min,
                      max: state.shipYearFilters.high || initialFilterData.shipLaunchDates.max,
                    }}
                    priceSliderStep={1}
                    onValuesChangeFinish={(low, high) =>
                      state.changeFilters(low, high, "year", initialFilterData.shipLaunchDates)
                    }
                    setIsDragging={setIsDragging}
                  />
                </div>
              </>
            )}
        </div>
        <div className={styles.footerContainer}>
          <p>{filteredCruisesCount} matches</p>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => {
                state.resetFilters()
              }}
              data-testid="filter-reset-button"
            >
              Reset
            </button>
            <button
              type="button"
              className={styles.applyButton}
              disabled={filteredCruisesCount === 0}
              onClick={() => {
                updateFilters(state.selectedFilters)
                onClose()
              }}
              data-testid="filter-apply-button"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    )
  },
)
